<template>
  <section class="compliance-page" aria-labelledby="compliance-summary-title">
    <div class="compliance-alert" role="note">
      <span class="alert-icon" aria-hidden="true">!</span>
      <p id="compliance-summary-title">
        <strong>Key Rules:</strong>
        Accumulated environment violations ≥20 points = Invalid Score. Confirmed cheating = Score Cancelled.
      </p>
    </div>

    <div class="compliance-content">
      <div class="guide-actions" aria-label="Exam preparation guides">
        <div class="guide-control">
          <button
            class="guide-button"
            type="button"
            aria-describedby="device-guide-tip"
          >
            Device Configuration Guide
            <span class="guide-chevron" aria-hidden="true">⌄</span>
          </button>
          <div id="device-guide-tip" class="guide-tip" role="tooltip">
            <strong>Device Rules</strong>
            <ul>
              <li>Use a desktop or laptop with a camera and microphone. Tablets are not allowed.</li>
              <li>Use the latest version of Google Chrome.</li>
            </ul>
            <p>
              Improper setup may interrupt the exam. See the full
              <button class="guide-link" type="button" @click="gotoDetail">
                Equipment Guide
              </button>.
            </p>
          </div>
        </div>

        <div class="guide-control">
          <button
            class="guide-button"
            type="button"
            aria-describedby="identity-guide-tip"
          >
            Identity Verification Guide
            <span class="guide-chevron" aria-hidden="true">⌄</span>
          </button>
          <div id="identity-guide-tip" class="guide-tip" role="tooltip">
            <strong>ID Check</strong>
            <p>
              Prepare a valid identity document showing your name and photo, such as a passport,
              student ID, driver’s license, or national ID. It will be photographed when you enter
              the exam room and deleted within 5 working days after scoring is completed.
            </p>
          </div>
        </div>
      </div>

      <div class="rule-grid">
        <article class="rule-card">
          <header class="rule-card-header">
            <span class="rule-number" aria-hidden="true">1</span>
            <h2>Environment Responsibility</h2>
          </header>
          <div class="rule-card-body">
            <p>
              Participants bear <strong>full responsibility</strong> for maintaining a compliant
              environment. Any breach will trigger <strong>automatic deductions</strong>.
            </p>
            <ul class="deduction-list">
              <li>
                <span>Stay quiet</span>
                <strong>−1 point / violation</strong>
              </li>
              <li>
                <span>Full face must stay visible</span>
                <strong>−2 points / violation</strong>
              </li>
              <li>
                <span>Maintain a stable internet connection</span>
                <strong>−1 point / violation</strong>
              </li>
            </ul>
            <p>
              Violation points <strong>cannot be revoked</strong>. If total deductions reach
              <strong>20 points</strong>, your environment will be deemed non-compliant and your
              score will be marked <strong>INVALID</strong>.
            </p>
          </div>
        </article>

        <article class="rule-card">
          <header class="rule-card-header">
            <span class="rule-number" aria-hidden="true">2</span>
            <h2>Operation Requirement</h2>
          </header>
          <div class="rule-card-body">
            <p>Two mandatory exam operations carry fixed penalties:</p>
            <ul class="deduction-list deduction-list--fixed">
              <li>
                <span>Fail to complete the mock test before the formal exam</span>
                <strong>−3 points</strong>
              </li>
              <li>
                <span>No manual submission by the end of the exam</span>
                <strong>−5 points</strong>
              </li>
            </ul>
            <p>These deductions will be applied to your official exam score.</p>
          </div>
        </article>

        <article class="rule-card">
          <header class="rule-card-header">
            <span class="rule-number" aria-hidden="true">3</span>
            <h2>Cheating Determination</h2>
          </header>
          <div class="rule-card-body">
            <p>
              The system flags high-risk abnormal exam behaviors. Invigilators conduct a
              multi-dimensional manual evidence review. Once cheating is confirmed,
              <strong>your entire score will be cancelled</strong>.
            </p>
            <div class="rule-divider" aria-hidden="true"></div>
            <h3>Cheating vs Violations</h3>
            <dl class="definition-list">
              <div>
                <dt>Environment Violations</dt>
                <dd>System-tracked points that affect exam validity.</dd>
              </div>
              <div>
                <dt>Cheating</dt>
                <dd>Confirmed misconduct resulting in score cancellation.</dd>
              </div>
            </dl>
          </div>
        </article>
      </div>

      <button class="policy-link" type="button" @click="gotoDetail1">
        View Full Exam Disciplinary Policy and Deduction Guidelines
        <span aria-hidden="true">↗</span>
      </button>
    </div>
  </section>
</template>

<script>
import "../../assets/common/font.css";

export default {
  name: "Announcements",
  methods: {
    gotoDetail() {
      window.open(
        "https://ap.learningfirst.io/Equipment%20Setting%20Guide%20English.pdf",
        "_blank"
      );
    },
    gotoDetail1() {
      window.open(
        "https://ap.learningfirst.tech/AM%20Exam%20Disciplinary%20Policy%20and%20Deduction%20Guidelines.pdf",
        "_blank"
      );
    },
  },
};
</script>

<style lang="less" scoped>
@danger: #df3344;
@danger-dark: #b71f31;
@ink: #1b213d;
@muted: #596079;
@surface: #ffffff;
@line: #e4e7f0;

.compliance-page {
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
  overflow: hidden;
  box-sizing: border-box;
  background: #f3f5f9;
  color: @ink;
  font-family: Montserrat-SemiBold, "Microsoft YaHei", sans-serif;
}

.compliance-alert {
  display: flex;
  flex-shrink: 0;
  align-items: center;
  gap: 12px;
  min-height: 48px;
  padding: 8px clamp(24px, 2.5vw, 40px);
  box-sizing: border-box;
  background: @danger;
  color: #fff9fa;
  font-size: 15px;
  line-height: 1.55;

  p {
    margin: 0;
  }

  strong {
    font-weight: 700;
  }
}

.alert-icon {
  display: inline-flex;
  flex: 0 0 20px;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: #ffd84d;
  color: #65200b;
  font-size: 13px;
  font-weight: 700;
}

.compliance-content {
  display: flex;
  flex: 1;
  flex-direction: column;
  min-height: 0;
  width: 100%;
  max-width: 1480px;
  margin: 0 auto;
  padding: 24px clamp(24px, 2.5vw, 40px) 20px;
  box-sizing: border-box;
}

.guide-actions {
  position: relative;
  z-index: 4;
  display: flex;
  flex-shrink: 0;
  flex-wrap: wrap;
  gap: 14px;
  margin-bottom: 24px;
}

.guide-control {
  position: relative;
}

.guide-button {
  display: inline-flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  min-height: 42px;
  padding: 9px 15px 9px 17px;
  border: 1px solid #aeb4c6;
  border-radius: 4px;
  background: @surface;
  color: @ink;
  font: inherit;
  font-size: 14px;
  cursor: default;
  transition: border-color 160ms ease, box-shadow 160ms ease, color 160ms ease;
}

.guide-chevron {
  color: #747c99;
  font-size: 17px;
  transform: translateY(-2px);
  transition: transform 160ms ease;
}

.guide-control:hover .guide-button,
.guide-control:focus-within .guide-button {
  border-color: #3d78d8;
  box-shadow: 0 0 0 3px rgba(61, 120, 216, 0.12);
  color: #234f98;
  outline: none;
}

.guide-control:hover .guide-chevron,
.guide-control:focus-within .guide-chevron {
  transform: translateY(1px) rotate(180deg);
}

.guide-tip {
  position: absolute;
  top: calc(100% + 10px);
  left: 0;
  width: min(410px, calc(100vw - 80px));
  padding: 18px 20px;
  box-sizing: border-box;
  border: 1px solid #d6dae6;
  border-radius: 5px;
  background: @surface;
  box-shadow: 0 16px 36px rgba(32, 43, 76, 0.16);
  color: @muted;
  font-size: 13px;
  line-height: 1.65;
  opacity: 0;
  visibility: hidden;
  pointer-events: none;
  transform: translateY(-5px);
  transition: opacity 160ms ease, transform 160ms ease, visibility 160ms ease;

  &::before {
    position: absolute;
    top: -6px;
    left: 24px;
    width: 10px;
    height: 10px;
    border-top: 1px solid #d6dae6;
    border-left: 1px solid #d6dae6;
    background: @surface;
    content: "";
    transform: rotate(45deg);
  }

  strong {
    display: block;
    margin-bottom: 6px;
    color: @ink;
    font-size: 14px;
  }

  p {
    margin: 0;
  }

  ul {
    margin: 6px 0 8px;
    padding-left: 20px;
  }
}

.guide-control:hover .guide-tip,
.guide-control:focus-within .guide-tip {
  opacity: 1;
  visibility: visible;
  pointer-events: auto;
  transform: translateY(0);
}

.guide-link,
.policy-link {
  padding: 0;
  border: 0;
  border-bottom: 1px solid currentColor;
  background: transparent;
  color: @danger;
  font: inherit;
  font-weight: 700;
  cursor: pointer;
}

.guide-link:focus-visible,
.policy-link:focus-visible {
  border-radius: 2px;
  outline: 3px solid rgba(61, 120, 216, 0.26);
  outline-offset: 3px;
}

.rule-grid {
  display: grid;
  flex: 1;
  min-height: 0;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: clamp(16px, 1.6vw, 24px);
  align-items: stretch;
}

.rule-card {
  display: flex;
  flex-direction: column;
  overflow: hidden;
  min-width: 0;
  min-height: 0;
  border: 1px solid @danger;
  border-radius: 5px;
  background: @surface;
}

.rule-card-header {
  display: flex;
  flex-shrink: 0;
  align-items: center;
  gap: 12px;
  min-height: 44px;
  padding: 8px 16px;
  box-sizing: border-box;
  background: @danger;
  color: #fff9fa;

  h2 {
    margin: 0;
    font-size: clamp(14px, 1vw, 17px);
    font-weight: 700;
    line-height: 1.3;
  }
}

.rule-number {
  display: inline-flex;
  flex: 0 0 24px;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border: 1px solid rgba(255, 249, 250, 0.62);
  border-radius: 50%;
  font-size: 13px;
  font-weight: 700;
}

.rule-card-body {
  flex: 1;
  min-height: 0;
  padding: clamp(16px, 1.3vw, 20px);
  color: #33394e;
  font-size: clamp(12px, 0.86vw, 14px);
  line-height: 1.5;

  p {
    margin: 0;
  }

  strong {
    color: @danger-dark;
    font-weight: 700;
  }

  h3 {
    margin: 0 0 8px;
    color: @ink;
    font-size: 15px;
  }
}

.deduction-list {
  display: grid;
  gap: 0;
  margin: 12px 0;
  padding: 0;
  border-top: 1px solid @line;
  list-style: none;

  li {
    display: grid;
    grid-template-columns: minmax(0, 1fr) auto;
    gap: 12px;
    align-items: start;
    padding: 8px 0;
    border-bottom: 1px solid @line;
  }

  strong {
    white-space: nowrap;
  }
}

.deduction-list--fixed li {
  grid-template-columns: minmax(0, 1fr);
  gap: 4px;

  strong {
    justify-self: start;
  }
}

.rule-divider {
  height: 1px;
  margin: 16px 0 12px;
  background: @line;
}

.definition-list {
  display: grid;
  gap: 12px;
  margin: 0;

  div {
    display: grid;
    gap: 2px;
  }

  dt {
    color: @ink;
    font-weight: 700;
  }

  dd {
    margin: 0;
    color: @muted;
  }
}

.policy-link {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  flex-shrink: 0;
  margin-top: 12px;
  font-size: 13px;
  line-height: 1.6;
}

@media (max-width: 1220px) {
  .rule-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .rule-card:last-child {
    grid-column: 1 / -1;
  }
}

@media (max-width: 820px) {
  .compliance-page {
    height: auto;
    min-height: 100%;
    overflow: visible;
  }

  .compliance-alert,
  .compliance-content {
    padding-right: 20px;
    padding-left: 20px;
  }

  .guide-actions,
  .rule-grid {
    grid-template-columns: minmax(0, 1fr);
  }

  .guide-actions {
    display: grid;
  }

  .guide-button {
    width: 100%;
  }

  .guide-tip {
    width: 100%;
  }

  .rule-card:last-child {
    grid-column: auto;
  }
}

@media (max-height: 760px) and (min-width: 1221px) {
  .compliance-content {
    padding-top: 16px;
    padding-bottom: 12px;
  }

  .guide-actions {
    margin-bottom: 16px;
  }

  .guide-button {
    min-height: 40px;
    padding-top: 8px;
    padding-bottom: 8px;
  }

  .rule-card-header {
    min-height: 40px;
    padding-top: 6px;
    padding-bottom: 6px;
  }

  .rule-card-body {
    padding: 16px;
    font-size: 12px;
    line-height: 1.45;
  }

  .deduction-list {
    margin: 8px 0;

    li {
      padding: 4px 0;
    }
  }

  .rule-divider {
    margin: 12px 0 8px;
  }

  .definition-list {
    gap: 8px;
  }

  .policy-link {
    margin-top: 8px;
    font-size: 12px;
  }
}

@media (prefers-reduced-motion: reduce) {
  .guide-button,
  .guide-chevron,
  .guide-tip {
    transition: none;
  }
}
</style>
